/**
 * Safety report form component.
 * 
 * ACT 8: Minimal safety/fraud/abuse report form.
 * 
 * Safety doctrine:
 * - No personal identity required
 * - No contact details (email, phone, address)
 * - No legal name, government ID, employer, income
 * - No social media handles or financial data
 * - Constrained report types
 * - Optional card ID with length limit
 * - Optional description with length limit (500 chars)
 * - Required acknowledgement checkbox
 * 
 * TODO(ACT 8): Emit report_started audit event when form is displayed.
 * TODO(ACT 8): Emit report_submitted audit event on successful submit.
 */
import { useState, useEffect } from 'react';
import {
  ReportType,
  ReportDraft,
  REPORT_TYPE_LABELS,
  REPORT_TYPE_DESCRIPTIONS,
  DESCRIPTION_MAX_LENGTH,
  CARD_ID_MAX_LENGTH,
  submitReport,
} from '@/api/reports';

/**
 * Report form props.
 */
export interface ReportFormProps {
  /** Initial related card ID if coming from card detail */
  initialCardId?: string;
  /** Callback on successful submission */
  onSuccess: (reportId: string) => void;
  /** Callback on error */
  onError: (error: string) => void;
}

/**
 * Report type select component.
 */
function ReportTypeSelect({
  value,
  onChange,
}: {
  value: ReportType | '';
  onChange: (type: ReportType) => void;
}) {
  const reportTypes: ReportType[] = [
    'safety_concern',
    'suspected_fraud',
    'inappropriate_content',
    'privacy_concern',
    'technical_issue',
    'other',
  ];

  return (
    <div className="form-group">
      <label htmlFor="report-type" className="form-label">
        Report Type <span className="required">*</span>
      </label>
      <select
        id="report-type"
        className="form-select"
        value={value}
        onChange={(e) => onChange(e.target.value as ReportType)}
        data-testid="report-type-select"
        required
      >
        <option value="">Select a type...</option>
        {reportTypes.map((type) => (
          <option key={type} value={type}>
            {REPORT_TYPE_LABELS[type]}
          </option>
        ))}
      </select>
      {value && REPORT_TYPE_DESCRIPTIONS[value] && (
        <p className="form-hint">{REPORT_TYPE_DESCRIPTIONS[value]}</p>
      )}
    </div>
  );
}

/**
 * Card ID input component.
 */
function CardIdInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="form-group">
      <label htmlFor="card-id" className="form-label">
        Related Card ID <span className="optional">(optional)</span>
      </label>
      <input
        type="text"
        id="card-id"
        className="form-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={CARD_ID_MAX_LENGTH}
        placeholder="e.g., card-001"
        data-testid="card-id-input"
      />
      <p className="form-hint">
        If this report is about a specific candidate card, enter its ID.
      </p>
    </div>
  );
}

/**
 * Description textarea component.
 */
function DescriptionInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const charCount = value.length;
  const isOverLimit = charCount > DESCRIPTION_MAX_LENGTH;

  return (
    <div className="form-group">
      <label htmlFor="description" className="form-label">
        Description <span className="optional">(optional)</span>
      </label>
      <textarea
        id="description"
        className={`form-textarea ${isOverLimit ? 'form-input-error' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={DESCRIPTION_MAX_LENGTH + 50} // Allow slight overfill for validation
        placeholder="Briefly describe the issue..."
        rows={4}
        data-testid="description-input"
      />
      <p className={`char-counter ${isOverLimit ? 'char-counter-error' : ''}`} data-testid="description-counter">
        {charCount}/{DESCRIPTION_MAX_LENGTH}
      </p>
    </div>
  );
}

/**
 * Acknowledgement checkbox component.
 */
function AcknowledgementCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="form-group">
      <label className="checkbox-label">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          data-testid="acknowledgement-checkbox"
        />
        <span className="checkbox-text">
          I confirm this report does not include personal contact information,
          financial details, or unnecessary identity data. <span className="required">*</span>
        </span>
      </label>
    </div>
  );
}

/**
 * Safety notice component.
 */
function SafetyNotice() {
  return (
    <div className="safety-notice" data-testid="safety-notice">
      <p className="safety-title">Before Submitting</p>
      <ul className="safety-list">
        <li>Do not include your real name, phone, or email</li>
        <li>Do not include financial information</li>
        <li>Do not include social media handles</li>
        <li>Provide only relevant context for the issue</li>
      </ul>
    </div>
  );
}

/**
 * Report form component.
 */
export function ReportForm({ initialCardId, onSuccess, onError }: ReportFormProps) {
  const [reportType, setReportType] = useState<ReportType | ''>('');
  const [cardId, setCardId] = useState(initialCardId || '');
  const [description, setDescription] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // TODO(ACT 8): Emit report_started audit event when form is displayed
  useEffect(() => {
    // TODO(ACT 8): await emitAuditEvent('report_started', { initialCardId });
  }, [initialCardId]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!reportType) {
      newErrors.reportType = 'Please select a report type';
    }

    if (description.length > DESCRIPTION_MAX_LENGTH) {
      newErrors.description = 'Description exceeds maximum length';
    }

    if (cardId.length > CARD_ID_MAX_LENGTH) {
      newErrors.cardId = 'Card ID exceeds maximum length';
    }

    if (!acknowledged) {
      newErrors.acknowledged = 'You must confirm before submitting';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const draft: ReportDraft = {
        reportType: reportType as ReportType,
        relatedCardId: cardId || undefined,
        description: description || undefined,
        acknowledged,
      };

      const response = await submitReport(draft);
      
      // TODO(ACT 8): Emit report_submitted audit event
      // TODO(ACT 8): await emitAuditEvent('report_submitted', { reportId: response.reportId, reportType });
      
      onSuccess(response.reportId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submission failed';
      onError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = reportType && acknowledged && description.length <= DESCRIPTION_MAX_LENGTH;

  return (
    <form
      className="report-form"
      onSubmit={handleSubmit}
      data-testid="report-form"
      noValidate
    >
      <SafetyNotice />

      <ReportTypeSelect value={reportType} onChange={setReportType} />
      {errors.reportType && (
        <p className="form-error" role="alert">{errors.reportType}</p>
      )}

      <CardIdInput value={cardId} onChange={setCardId} />
      {errors.cardId && (
        <p className="form-error" role="alert">{errors.cardId}</p>
      )}

      <DescriptionInput value={description} onChange={setDescription} />
      {errors.description && (
        <p className="form-error" role="alert">{errors.description}</p>
      )}

      <AcknowledgementCheckbox checked={acknowledged} onChange={setAcknowledged} />
      {errors.acknowledged && (
        <p className="form-error" role="alert">{errors.acknowledged}</p>
      )}

      <button
        type="submit"
        className="btn btn-primary"
        disabled={!isValid || isSubmitting}
        data-testid="submit-button"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Report'}
      </button>

      <style>{`
        .report-form {
          display: flex;
          flex-direction: column;
          gap: var(--spacing);
        }
        .form-group {
          margin-bottom: var(--spacing);
        }
        .form-label {
          display: block;
          font-weight: 500;
          margin-bottom: 6px;
          color: var(--color-text);
          font-size: 0.9rem;
        }
        .form-label .required {
          color: var(--color-error, #d32f2f);
        }
        .form-label .optional {
          color: var(--color-text-muted);
          font-weight: normal;
          font-size: 0.8rem;
        }
        .form-input,
        .form-select,
        .form-textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          font-size: 0.9rem;
          background: var(--color-background);
          color: var(--color-text);
        }
        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: var(--color-accent);
        }
        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }
        .form-input-error {
          border-color: var(--color-error, #d32f2f);
        }
        .form-hint {
          margin-top: 4px;
          font-size: 0.8rem;
          color: var(--color-text-muted);
        }
        .form-error {
          margin-top: 4px;
          font-size: 0.8rem;
          color: var(--color-error, #d32f2f);
        }
        .char-counter {
          margin-top: 4px;
          font-size: 0.75rem;
          color: var(--color-text-muted);
          text-align: right;
        }
        .char-counter-error {
          color: var(--color-error, #d32f2f);
        }
        .checkbox-label {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          cursor: pointer;
        }
        .checkbox-label input {
          margin-top: 4px;
        }
        .checkbox-text {
          font-size: 0.85rem;
          color: var(--color-text-muted);
          line-height: 1.4;
        }
        .checkbox-text .required {
          color: var(--color-error, #d32f2f);
        }
        .safety-notice {
          padding: var(--spacing);
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          margin-bottom: var(--spacing);
        }
        .safety-title {
          font-weight: 600;
          margin-bottom: var(--spacing-sm);
          font-size: 0.9rem;
        }
        .safety-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .safety-list li {
          font-size: 0.8rem;
          color: var(--color-text-muted);
          padding: 3px 0;
          padding-left: 16px;
          position: relative;
        }
        .safety-list li::before {
          content: "•";
          position: absolute;
          left: 4px;
          color: var(--color-warning, #e65100);
        }
        .btn {
          padding: 12px 24px;
          border-radius: var(--radius);
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          border: none;
          margin-top: var(--spacing);
        }
        .btn-primary {
          background: var(--color-primary, #1976d2);
          color: white;
        }
        .btn-primary:hover:not(:disabled) {
          background: var(--color-primary-dark, #1565c0);
        }
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </form>
  );
}